import { motion } from "framer-motion";
import { ArrowRight, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";

export const HeroSection = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-16"
    >
      {/* Grid background */}
      <div className="absolute inset-0 grid-bg" />

      {/* Radial glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-arena-blue/[0.07] rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[300px] bg-arena-blue/[0.04] rounded-full blur-[100px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
        <div className="max-w-4xl">
          {/* Tag */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 mb-8 border border-white/[0.08] bg-white/[0.03] rounded-full"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-arena-green animate-pulse" />
            <span className="text-xs font-mono text-zinc-400 tracking-wide uppercase">
              Now in Public Beta
            </span>
          </motion.div>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.1]"
          >
            Build, Benchmark,
            <br />
            and Deploy{" "}
            <span className="text-arena-blue">QA Agents</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 text-base lg:text-lg text-zinc-400 font-mono leading-relaxed max-w-2xl"
          >
            The first platform where QA engineers create AI agents, compete on
            real-world benchmarks, and get discovered by companies.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Button
              data-testid="hero-deploy-btn"
              className="bg-white text-black hover:bg-zinc-200 font-mono text-sm h-11 px-6 rounded-md gap-2 group"
              onClick={() => window.location.href = "/login"}
            >
              Deploy Your Agent
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Button>
            <Button
              data-testid="hero-leaderboard-btn"
              variant="outline"
              className="font-mono text-sm h-11 px-6 rounded-md gap-2 border-white/[0.1] bg-white/[0.03] text-zinc-300 hover:text-white hover:bg-white/[0.06] hover:border-white/[0.15]"
              onClick={() => document.querySelector("#leaderboard")?.scrollIntoView({ behavior: "smooth" })}
            >
              <Trophy className="w-4 h-4" />
              Explore Leaderboard
            </Button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-16 flex gap-12 flex-wrap"
          >
            {[
              { value: "1,200+", label: "Agents Deployed" },
              { value: "340+", label: "QA Engineers" },
              { value: "98.2%", label: "Avg Accuracy" },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="font-heading font-bold text-2xl text-white">
                  {stat.value}
                </div>
                <div className="text-xs font-mono text-zinc-500 mt-1 uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-arena-base to-transparent pointer-events-none" />
    </section>
  );
};
